import React, { useState, useEffect } from "react";
import colors from "../ColorConfig/colorconfig";
import SEO from "../../AASeo/Seo";

const organization = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "IDLBAY Private Limited",
  alternateName: "IDLBAY",
  url: "https://idlbay.com",
  logo: "https://idlbay.com/idlbaylogo.png",
  sameAs: [
    "https://www.linkedin.com/company/idlbay/",
    "https://x.com/idlbayPvtLtd",
    "https://www.instagram.com/idlbay.official?igsh=dnIxMm50cmdxNDNy",
    "https://www.facebook.com/profile.php?id=61582746578911",
    "https://www.youtube.com/channel/UCDSb9__-mSLqeyuZYelakcw",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+91-9840841840",
    contactType: "Customer Support",
    areaServed: "IN",
    availableLanguage: ["English", "Tamil"],
  },
  address: {
    "@type": "PostalAddress",
    streetAddress:
      "Office No.1, First Floor, Door No.1, B Narasimhan 3rd Street",
    addressLocality: "T-Nagar, Chennai",
    addressRegion: "Tamil Nadu",
    postalCode: "600017",
    addressCountry: "IN",
  },
};

function PrivacyPolicy() {
  const [privacyData, setPrivacyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPrivacyData();

    // Check if device is mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  const fetchPrivacyData = async () => {
    try {
      const response = await fetch(
        "https://api.dremerz.net/api/DZ_8923_Idlbay_PrivacyPolicy_PrivacyPolicyList"
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setPrivacyData(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      console.error("Error fetching privacy policy data:", err);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          backgroundColor: colors.white,
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div>Loading privacy policy...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          backgroundColor: colors.white,
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          padding: "2rem",
        }}
      >
        <div style={{ color: colors.error, marginBottom: "1rem" }}>
          Error loading privacy policy: {error}
        </div>
        <button
          onClick={fetchPrivacyData}
          style={{
            padding: "0.5rem 1rem",
            backgroundColor: `var(--color-primary)`,
            color: colors.white,
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Privacy Policy | IDLBAY"
        description="Read IDLBAY's privacy policy to learn how we collect, use, and protect your personal information and data."
        keywords="privacy policy, IDLBAY, data protection, personal information, user privacy"
        url="https://idlbay.com/privacypolicy"
        organization={organization}
      />
      <div style={{ backgroundColor: colors.white, Height: "100vh" }}>
        {/* Header Section */}
        <div
          style={{
            backgroundColor: colors.bodyBlack,
            paddingTop: isMobile ? "1rem" : "6rem", // Reduced from 6rem to 4rem on mobile
            paddingBottom: isMobile ? "2rem" : "4rem", // Reduced from 4rem to 2rem on mobile
            textAlign: "center",
          }}
        >
          <div
            style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 1rem" }}
          >
            <h1
              style={{
                fontSize: isMobile ? "2rem" : "clamp(2rem, 5vw, 3.5rem)", // Adjusted for mobile
                fontWeight: "700",
                color: colors.white,
                marginBottom: isMobile ? "0.8rem" : "1rem", // Reduced margin on mobile
                lineHeight: "1.2",
              }}
            >
              Privacy Policy
            </h1>
            <div
              style={{
                width: "80px",
                height: "4px",
                backgroundColor: `var(--color-primary)`,
                margin: isMobile ? "0 auto 1rem auto" : "0 auto 1.5rem auto", // Reduced margin on mobile
              }}
            ></div>
            <h2
              style={{
                fontSize: isMobile ? "1rem" : "1.1rem", // Slightly smaller on mobile
                color: colors.gray,
                maxWidth: "900px",
                margin: "0 auto",
                lineHeight: "1.6",
              }}
            >
              Your privacy is important to us. Learn how we collect, use, and
              protect your data.
            </h2>
          </div>
        </div>

        {/* Content Section */}
        <div
          style={{
            maxWidth: "900px",
            margin: "0 auto",
            padding: isMobile ? "2rem 1rem" : "3rem 1rem", // Reduced padding on mobile
          }}
        >
          <div
            style={{
              borderRadius: "12px",
              padding: isMobile ? "1.5rem" : "2rem", // Reduced padding on mobile
            }}
          >
            {privacyData.length > 0 ? (
              privacyData.map((policy, index) => (
                <section
                  key={policy.id}
                  style={{
                    marginBottom: isMobile ? "2rem" : "2.5rem", // Reduced margin on mobile
                  }}
                >
                  <h2
                    style={{
                      fontSize: isMobile ? "1.4rem" : "1.8rem", // Smaller font on mobile
                      fontWeight: "600",
                      color: colors.bodyBlack,
                      marginBottom: isMobile ? "0.8rem" : "1rem", // Reduced margin on mobile
                      borderLeft: `4px solid var(--color-primary)`,
                      paddingLeft: "1rem",
                    }}
                  >
                    {index + 1}. {policy.title}
                  </h2>
                  <h3
                    style={{
                      color: colors.bodyBlack,
                      lineHeight: "1.7",
                      fontSize: isMobile ? "0.95rem" : "1rem", // Slightly smaller text on mobile
                    }}
                  >
                    {policy.description.split("\n").map(
                      (paragraph, i) =>
                        paragraph.trim() && (
                          <p
                            key={i}
                            style={{
                              marginBottom: isMobile ? "0.8rem" : "1rem", // Reduced margin on mobile
                            }}
                          >
                            {paragraph.trim()}
                          </p>
                        )
                    )}
                  </h3>
                </section>
              ))
            ) : (
              <div style={{ textAlign: "center", padding: "2rem" }}>
                No privacy policy available at this time.
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default PrivacyPolicy;
